/**
 * Date Helper Functions for CarBooking Module
 * Funciones auxiliares para manejo de fechas
 */

/**
 * Obtiene la fecha actual al inicio del día
 * @returns {Date} Fecha actual a las 00:00:00
 */
export const today = () => startOfDay(new Date());

/**
 * Obtiene el nombre del día de la semana en español
 * @param {Date} d - Fecha
 * @returns {string} Nombre del día en español (ej: "lunes")
 */
export const weekdayLongES = (d) =>
    d.toLocaleDateString("es-MX", { weekday: "long" });

/**
 * Normaliza una fecha al inicio del día (00:00:00)
 * @param {Date} d - Fecha a normalizar
 * @returns {Date} Fecha normalizada
 */
export function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

/**
 * Obtiene el lunes de la semana de una fecha dada
 * @param {Date} d - Fecha de referencia
 * @returns {Date} Lunes de la semana
 */
export function getMonday(d) {
    const date = new Date(d);
    const day = date.getDay();
    const diff = (day === 0 ? -6 : 1) - day; // mover al lunes
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
    return date;
}

/**
 * Formatea una fecha en formato YYYY-MM-DD para inputs HTML
 * @param {Date} d - Fecha a formatear
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
export function formatDateLocal(d) {
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // YYYY-MM-DD
}

/**
 * Obtiene el número de semana ISO de una fecha
 * @param {Date} date - Fecha
 * @returns {number} Número de semana (1-53)
 */
export function getISOWeek(date) {
    const d = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

/**
 * Verifica si una fecha es fin de semana
 * @param {Date} date - Fecha a verificar
 * @returns {boolean} true si es sábado o domingo
 */
export function isWeekend(date) {
    const day = date.getDay(); // 0 = domingo, 6 = sábado
    return day === 0 || day === 6;
}

/**
 * Combina una fecha (YYYY-MM-DD) con una hora (HH:mm) en un objeto Date
 * @param {string} dateStr - Fecha en formato YYYY-MM-DD
 * @param {string} timeStr - Hora en formato HH:mm
 * @returns {Date} Fecha con hora
 */
export function toDateWithTime(dateStr, timeStr) {
    const [y, m, d] = dateStr.split("-").map(Number);
    const [hh = 0, mm = 0] = (timeStr || "00:00").split(":").map(Number);
    return new Date(y, m - 1, d, hh, mm, 0, 0);
}

/**
 * Normaliza un valor a solo fecha (sin hora) como objeto Date
 * @param {Date|string} value - Valor a normalizar
 * @returns {Date|null} Fecha normalizada o null si es inválido
 */
export function normalizeDateOnly(value) {
    if (!value) return null;
    if (value instanceof Date) {
        const d = new Date(value);
        d.setHours(0, 0, 0, 0);
        return d;
    }
    const s = String(value);
    const parts = s.slice(0, 10).split("-");
    if (parts.length !== 3) return null;
    const [y, m, d] = parts.map(Number);
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d);
    dt.setHours(0, 0, 0, 0);
    return dt;
}
